import { Button, Typography } from "matsuri-ui"
import { css } from "@emotion/react"
import CloseIcon from "@mui/icons-material/Close"
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded"
import {
    Card,
    CardTitle,
    Content,
    ExampleList,
    ExampleRow,
    Main,
    PropertyBar,
    QuestionLead,
    QuestionTitle,
    StepLabel,
    TitleAccent
} from "./styled"
import { MobileLayout } from "./utils"
import { Header } from "@/components/Header/Header"
import { MobileAppHeader } from "@/components/Header/MobileAppHeader"
import { ReportFooter } from "@/components/Footer/ReportFooter"
import { useTranslation } from "@/hooks/useTranslation"

interface Props {
    step: 1 | 2
    // 1文＝1翻訳単位にするため、文ごとに分けて渡す
    lead: string[]
    examplesTitle: string
    examples: string[]
    yesLabel: string
    noLabel: string
    listingName?: string
    layout: MobileLayout
    onYes: () => void
    onNo: () => void
    onPrev: () => void
    onClose: () => void
}

/**
 * 2択質問の共通 View（Q1: 廃棄してよいか / Q2: 貴重品か）。
 * 清掃員にカテゴリを判断させないため、該当する具体例を先に見せてから「当てはまる / 当てはまらない」を選ばせる。
 */
export const QuestionView = ({
    step,
    lead,
    examplesTitle,
    examples,
    yesLabel,
    noLabel,
    listingName,
    layout,
    onYes,
    onNo,
    onPrev,
    onClose
}: Props) => {
    const { t } = useTranslation()

    return (
        <Main style={layout.rootProps.style}>
            <Header {...layout.headerProps}>
                <MobileAppHeader
                    title={
                        <Typography variant="h4">
                            {t("忘れ物を報告")}
                        </Typography>
                    }
                    onPrev={onPrev}
                    onNext={onClose}
                    nextIcon={<CloseIcon />}
                />
            </Header>

            <div {...layout.bodyProps}>
                {listingName && (
                    <PropertyBar>
                        <ApartmentRoundedIcon style={{ fontSize: 18 }} />
                        <span>{listingName}</span>
                    </PropertyBar>
                )}

                <Content>
                    <div>
                        <StepLabel>
                            {t("Q{{step}} / 2", { params: { step } })}
                        </StepLabel>
                        <QuestionTitle
                            css={css`
                                margin-top: 4px;
                            `}
                        >
                            {t("この中に当てはまりますか？")}
                        </QuestionTitle>
                        <QuestionLead
                            css={css`
                                margin-top: 8px;
                            `}
                        >
                            {lead.map(sentence => (
                                <div key={sentence}>{sentence}</div>
                            ))}
                        </QuestionLead>
                    </div>

                    <Card elevation={1}>
                        <CardTitle>
                            <TitleAccent />
                            {examplesTitle}
                        </CardTitle>
                        <ExampleList>
                            {examples.map(example => (
                                <ExampleRow key={example}>
                                    {t(example)}
                                </ExampleRow>
                            ))}
                        </ExampleList>
                    </Card>
                </Content>
            </div>

            <ReportFooter {...layout.footerProps}>
                <Button
                    variant="filled"
                    color="primary"
                    fullWidth
                    onClick={onYes}
                >
                    {yesLabel}
                </Button>
                <Button
                    variant="outlined"
                    fullWidth
                    onClick={onNo}
                    css={css`
                        margin-top: 8px;
                    `}
                >
                    {noLabel}
                </Button>
            </ReportFooter>
        </Main>
    )
}
